import { Link } from 'react-router';
import { MoreVertical, Trash2 } from 'lucide-react';
import type { DashboardRunResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDuration, formatStarted } from '@/lib/format';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { StatusPill, type StatusPillStatus } from '@/components/ui/status-pill';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface WorkflowHistoryTableProps {
  runs: DashboardRunResponse[];
  onDelete?: (runId: string) => void;
}

function formatCost(cost: unknown): string | null {
  if (typeof cost !== 'number' || cost <= 0) return null;
  return `$${cost.toFixed(4)}`;
}

export function WorkflowHistoryTable({
  runs,
  onDelete,
}: WorkflowHistoryTableProps): React.ReactElement {
  if (runs.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-xs text-text-tertiary">No history</span>
      </div>
    );
  }

  const headers: { label: string; widthClass?: string; align?: 'right' }[] = [
    { label: 'Workflow' },
    { label: 'Status', widthClass: 'w-28' },
    { label: 'Codebase' },
    { label: 'Started', widthClass: 'w-32' },
    { label: 'Duration', widthClass: 'w-20' },
    { label: 'Cost', widthClass: 'w-20' },
    { label: '', widthClass: 'w-10' },
  ];

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-surface">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-surface-inset text-left">
            {headers.map((h, i) => (
              <th
                key={i}
                className={cn(
                  'px-3 py-2.5 font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary',
                  h.widthClass,
                  h.align === 'right' && 'text-right'
                )}
              >
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {runs.map((run, i) => {
            const cost = formatCost(run.metadata?.total_cost_usd);
            return (
              <tr
                key={run.id}
                className={cn(
                  'transition-colors hover:bg-surface-elevated',
                  i < runs.length - 1 && 'border-b border-border/60',
                  run.status === 'failed' && 'border-l-2 border-l-destructive'
                )}
              >
                <td className="px-3 py-2.5">
                  <Link
                    to={`/workflows/runs/${run.id}`}
                    className="block truncate font-mono text-[12px] font-medium text-text-primary hover:text-accent-bright"
                  >
                    {run.workflow_name}
                  </Link>
                  {run.user_message && (
                    <p className="max-w-[300px] truncate text-[11px] italic text-text-tertiary">
                      {run.user_message}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <StatusPill status={run.status as StatusPillStatus} />
                </td>
                <td className="truncate px-3 py-2.5 font-mono text-[11.5px] text-text-secondary">
                  {run.codebase_name ?? '—'}
                </td>
                <td className="px-3 py-2.5 font-mono text-[11.5px] text-text-tertiary">
                  {formatStarted(run.started_at)}
                </td>
                <td className="px-3 py-2.5 font-mono text-[11.5px] text-text-tertiary">
                  {formatDuration(run.started_at, run.completed_at)}
                </td>
                <td className="px-3 py-2.5 font-mono text-[11.5px] text-flame-hi">{cost ?? '—'}</td>
                <td className="px-1 py-2 text-right">
                  {onDelete && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex size-7 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-surface-elevated hover:text-text-primary"
                          aria-label={`Actions for ${run.workflow_name}`}
                        >
                          <MoreVertical className="size-3.5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="end" sideOffset={4} className="w-44 p-1">
                        <Link
                          to={`/workflows/runs/${run.id}`}
                          className="flex w-full items-center rounded-md px-2 py-1.5 text-xs text-text-secondary transition-colors hover:bg-surface-elevated hover:text-text-primary"
                        >
                          View run
                        </Link>
                        <ConfirmDialog
                          trigger={
                            <button
                              type="button"
                              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text-secondary transition-colors hover:bg-error/10 hover:text-error"
                            >
                              <Trash2 className="size-3" />
                              Delete run
                            </button>
                          }
                          title="Delete workflow run?"
                          description={
                            <>
                              Permanently delete the run record for{' '}
                              <strong>{run.workflow_name}</strong> and its events. This cannot be
                              undone.
                            </>
                          }
                          confirmLabel="Delete"
                          onConfirm={(): void => {
                            onDelete(run.id);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
